import React from 'react';

import { Box, BoxProps, Flex, Select, Space, Spoiler, Text, Title } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { InfoComponent } from 'components/InfoComponent';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { SelectItem } from 'components/SelectItem';
import { useTranslation } from 'react-i18next';
import { ConversationHandoffService } from 'services/conversation-handoff-service';
import { NotificationService } from 'services/notification-service';
import { Conversation, HandoffStatus } from 'shared/interfaces/Conversation';
import { ConversationHandoffTypeBadge, HANDOFF_STATUS_SELECT_DATA } from 'shared/utils/conversation';

interface ConversationHandoffContentProps extends BoxProps {
  conversation: Conversation;
  onConversationUpdate: (updatedConversation: Conversation) => void;
}

export function ConversationHandoffContent({
  conversation,
  onConversationUpdate,
  ...boxProps
}: ConversationHandoffContentProps) {
  const { t } = useTranslation();

  const updateConversationHandoffStatusMutation = useMutation({
    mutationFn: ConversationHandoffService.updateStatus,
    onSuccess: (updatedConversation: Conversation) => onConversationUpdate(updatedConversation),
    onError: () => NotificationService.showResult(false, t('FEED_BACK.CONVERSATION.FAILURE.UPDATE_HANDOFF_STATUS'))
  });

  if (!conversation.handoffData) return null;

  const handoffData = conversation.handoffData;

  return (
    <Flex direction="row" align="flex-start" justify="space-between" {...boxProps}>
      <Box>
        <Flex gap={5} align="center">
          <Title order={6}>{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.DETAILS.TITLE')}</Title>
          <InfoComponent contentTranslationKey="SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.DETAILS.INFO" />
        </Flex>

        <Space h={5} />
        <Flex direction="row">
          <Box maw={300}>
            <Flex gap="xs" align="flex-start" w="100%">
              <Text fw={500}>{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.DETAILS.EMAIL')}</Text>
              <Text truncate>{handoffData.email}</Text>
            </Flex>
            {handoffData.name && (
              <Flex gap="xs" align="flex-start" mt={5} w="100%">
                <Text fw={500}>{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.DETAILS.NAME')}</Text>
                <Text truncate>{handoffData.name}</Text>
              </Flex>
            )}
            {handoffData.phone && (
              <Flex gap="xs" align="flex-start" mt={5} w="100%">
                <Text fw={500}>{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.DETAILS.PHONE')}</Text>
                <Text truncate>{handoffData.phone}</Text>
              </Flex>
            )}
          </Box>
          <Space w="xl" />
          {handoffData.reason && (
            <Flex gap="xs" align="flex-start" w="100%">
              <Text fw={500}>{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.DETAILS.REASON')}</Text>
              <Spoiler
                maxHeight={35}
                maw={400}
                showLabel={t('APP.ACTIONS.SHOW_MORE')}
                hideLabel={t('APP.ACTIONS.HIDE')}>
                <Text>{handoffData.reason}</Text>
              </Spoiler>
            </Flex>
          )}
        </Flex>
      </Box>

      <Flex direction="column" align="flex-start" justify="space-between" gap="sm">
        <Select
          label={
            <InputLabelWithInfo
              label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.LABEL')}
              infoContentTranslationKey="SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.INFO"
            />
          }
          itemComponent={SelectItem}
          data={HANDOFF_STATUS_SELECT_DATA.filter((data) => data.value !== HandoffStatus.ALL)}
          onChange={(value) =>
            updateConversationHandoffStatusMutation.mutate({
              conversationId: conversation._id,
              status: value as HandoffStatus
            })
          }
          value={handoffData.status}
        />
        <ConversationHandoffTypeBadge type={handoffData.type} />
      </Flex>
    </Flex>
  );
}
